import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Customer Pages
import Landing from "./pages/Landing";
import Services from "./pages/Services";
import Booking from "./pages/Booking";
import MyBookings from "./pages/MyBookings";
import CustomerLogin from "./pages/CustomerLogin";
import CustomerRegister from "./pages/CustomerRegister";

// Staff Pages
import StaffLogin from "./pages/staff/StaffLogin";
import StaffDashboard from "./pages/staff/StaffDashboard";
import StaffBookings from "./pages/staff/StaffBookings";
import StaffServices from "./pages/staff/StaffServices";
import StaffCustomers from "./pages/staff/StaffCustomers";
import StaffAvailability from "./pages/staff/StaffAvailability";
import StaffAuthCallback from "./pages/staff/StaffAuthCallback";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CurrencyProvider } from "./context/CurrencyContext";

// Protected Route for customers
const CustomerProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-terracotta"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route for staff
const StaffProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage"></div>
      </div>
    );
  }
  
  if (!user || user.role !== "staff") {
    return <Navigate to="/staff/login" replace />;
  }
  
  return children;
};

// Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check for session_id in hash (Google OAuth callback for staff)
  if (location.hash?.includes('session_id=')) {
    return <StaffAuthCallback />;
  }
  
  return (
    <Routes>
      {/* ==================== CUSTOMER PORTAL ==================== */}
      <Route path="/" element={<Landing />} />
      <Route path="/services" element={<Services />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/booking/:serviceId" element={<Booking />} />
      <Route path="/login" element={<CustomerLogin />} />
      <Route path="/register" element={<CustomerRegister />} />
      <Route path="/my-bookings" element={
        <CustomerProtectedRoute>
          <MyBookings />
        </CustomerProtectedRoute>
      } />
      
      {/* ==================== STAFF PORTAL ==================== */}
      <Route path="/staff" element={<Navigate to="/staff/login" replace />} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff/dashboard" element={
        <StaffProtectedRoute>
          <StaffDashboard />
        </StaffProtectedRoute>
      } />
      <Route path="/staff/bookings" element={
        <StaffProtectedRoute>
          <StaffBookings />
        </StaffProtectedRoute>
      } />
      <Route path="/staff/services" element={
        <StaffProtectedRoute>
          <StaffServices />
        </StaffProtectedRoute>
      } />
      <Route path="/staff/customers" element={
        <StaffProtectedRoute>
          <StaffCustomers />
        </StaffProtectedRoute>
      } />
      <Route path="/staff/availability" element={
        <StaffProtectedRoute>
          <StaffAvailability />
        </StaffProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <div className="grain-overlay" />
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
